import React, { useMemo, useState } from "react";
import { Calculator, TrendingUp, Calendar, Coins } from "lucide-react";

// Simple but persuasive franchise revenue calculator.
// All numbers are conservative estimates — clearly labeled as "estimativa".
const PRICE_PER_CUP = 4.5; // €
const COST_PER_CUP = 1.2; // € (polpa + copo + água)
const DAYS_PER_MONTH = 26;

export const RevenueCalculator = () => {
    const [cupsPerDay, setCupsPerDay] = useState(80);

    const stats = useMemo(() => {
        const dailyRevenue = cupsPerDay * PRICE_PER_CUP;
        const dailyCost = cupsPerDay * COST_PER_CUP;
        const dailyMargin = dailyRevenue - dailyCost;
        const monthlyRevenue = dailyRevenue * DAYS_PER_MONTH;
        const monthlyMargin = dailyMargin * DAYS_PER_MONTH;
        const annualRevenue = monthlyRevenue * 12;
        const marginPct = Math.round((dailyMargin / dailyRevenue) * 100);
        return {
            dailyRevenue,
            monthlyRevenue,
            monthlyMargin,
            annualRevenue,
            marginPct,
        };
    }, [cupsPerDay]);

    const fmt = (n) =>
        new Intl.NumberFormat("pt-PT", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
        }).format(n);

    return (
        <div
            data-testid="revenue-calculator"
            className="relative rounded-2xl bg-white p-6 md:p-10 shadow-pop ring-1 ring-brand-green/15 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none"
            />
            <div className="relative">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            <Calculator className="h-3.5 w-3.5" />
                            Calculadora de receita
                        </span>
                        <h3 className="mt-2 font-display font-medium text-2xl md:text-3xl text-brand-ink leading-tight">
                            Quantos copos vendes por dia?
                        </h3>
                        <p className="mt-1 text-sm text-brand-ink-soft max-w-md">
                            Move o seletor e descobre o potencial do teu ponto de venda.
                        </p>
                    </div>
                    <div className="rounded-2xl bg-brand-green-soft px-4 py-3 text-center">
                        <div className="text-[11px] uppercase tracking-widest font-bold text-brand-green">
                            Margem média
                        </div>
                        <div className="font-display font-medium text-2xl text-brand-ink">
                            {stats.marginPct}%
                        </div>
                    </div>
                </div>

                <div className="mt-8">
                    <div className="flex items-baseline justify-between">
                        <span className="text-sm text-brand-ink-soft">Copos / dia</span>
                        <span
                            className="font-display font-medium text-5xl text-brand-green"
                            data-testid="calc-cups-display"
                        >
                            {cupsPerDay}
                        </span>
                    </div>
                    <input
                        type="range"
                        min={20}
                        max={300}
                        step={10}
                        value={cupsPerDay}
                        onChange={(e) => setCupsPerDay(Number(e.target.value))}
                        data-testid="calc-cups-slider"
                        aria-label="Copos por dia"
                        className="mt-3 w-full h-2 rounded-full appearance-none cursor-pointer bg-brand-green/15 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-green [&::-webkit-slider-thumb]:shadow-pop [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-brand-green [&::-moz-range-thumb]:border-0"
                    />
                    <div className="mt-2 flex justify-between text-xs text-brand-ink-soft">
                        <span>20</span>
                        <span>150</span>
                        <span>300</span>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Stat
                        icon={Coins}
                        label="Faturação diária"
                        value={fmt(stats.dailyRevenue)}
                        accent="text-brand-orange"
                        bg="bg-brand-orange-soft"
                        testid="calc-daily-revenue"
                    />
                    <Stat
                        icon={Calendar}
                        label="Faturação mensal"
                        value={fmt(stats.monthlyRevenue)}
                        accent="text-brand-green"
                        bg="bg-brand-green-soft"
                        testid="calc-monthly-revenue"
                    />
                    <Stat
                        icon={TrendingUp}
                        label="Lucro mensal estimado"
                        value={fmt(stats.monthlyMargin)}
                        accent="text-white"
                        bg="bg-brand-ink"
                        white
                        testid="calc-monthly-margin"
                    />
                </div>

                <div className="mt-6 rounded-2xl bg-brand-cream px-4 py-3 text-xs text-brand-ink-soft leading-relaxed">
                    <span className="font-bold text-brand-ink">Estimativa:</span> baseado em preço
                    médio de {fmt(PRICE_PER_CUP)}/copo e custo médio de {fmt(COST_PER_CUP)}/copo
                    (polpa + copo + água), com {DAYS_PER_MONTH} dias úteis por mês. Os teus números
                    reais podem variar consoante localização, modelo e operação.
                </div>

                <a
                    href="#contacto"
                    data-testid="calc-cta-falar-connosco"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white px-6 py-4 text-base font-semibold transition-all shadow-pop hover:translate-y-[-2px]"
                >
                    Quero falar com um especialista
                </a>
            </div>
        </div>
    );
};

const Stat = ({ icon: Icon, label, value, accent, bg, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-4 transition-all hover:-translate-y-0.5`}
    >
        <Icon className={`h-4 w-4 ${accent}`} />
        <div className={`mt-2 text-[11px] uppercase tracking-widest font-bold ${white ? "text-white/70" : "text-brand-ink-soft"}`}>
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-xl md:text-2xl ${white ? "text-white" : "text-brand-ink"}`}
        >
            {value}
        </div>
    </div>
);
